/**
 * WizardGeneratingOverlay  (default export)
 *
 * Full-panel indeterminate progress overlay. Used when an AI generation or
 * async operation is in-flight and no progress percentage is available.
 *
 * Props
 * ─────
 * title       string          – Main heading. Default: 'Working…'
 * subtitle    string          – Secondary line beneath the heading (optional)
 * icon        node            – Custom icon inside the circle. Default: AutoAwesomeIcon
 * showWarning string          – Caption below the progress bar.
 *                               Pass false/null to suppress. Default: standard message.
 * minHeight   number|string   – Override panel min-height. Default: 260
 *
 *
 * WizardSaveProgressOverlay  (named export)
 *
 * Full-panel overlay for bulk save / Jira-link operations where a determinate
 * progress percentage is available (saving phase) or an indeterminate bar is
 * needed (linking phase).
 *
 * Props
 * ─────
 * savePhase     string   – One of SAVE_PHASE constants
 * saveProgress  object   – { current, total, summary }  (null during LINKING)
 * SAVE_PHASE    object   – The SAVE_PHASE constants object
 *
 * Usage
 * ─────
 * import WizardGeneratingOverlay, { WizardSaveProgressOverlay } from './WizardGeneratingOverlay';
 *
 * // Indeterminate — AI generation
 * <WizardGeneratingOverlay title="Generating Jira Stories…" subtitle="…" />
 *
 * // Determinate — bulk save
 * <WizardSaveProgressOverlay savePhase={savePhase} saveProgress={saveProgress} SAVE_PHASE={SAVE_PHASE} />
 */

import AutoAwesomeIcon  from '@mui/icons-material/AutoAwesome';
import LinkIcon         from '@mui/icons-material/Link';
import { Box, CircularProgress, LinearProgress, Typography } from '@mui/material';

const WizardGeneratingOverlay = ({
  title = 'Working…',
  subtitle,
  icon,
  showWarning,
  minHeight = 260,
}) => {
  const warningText = showWarning === undefined ? "Please don't close this window" : showWarning;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        gap: 3,
        minHeight,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'primary.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: 'primary.200',
        }}
      >
        {icon ?? <AutoAwesomeIcon sx={{ fontSize: 28, color: 'primary.main' }} />}
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ width: '100%', maxWidth: 320 }}>
        <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
      </Box>

      {warningText && (
        <Typography variant="caption" color="text.secondary">
          {warningText}
        </Typography>
      )}
    </Box>
  );
};

// ── WizardSaveProgressOverlay ─────────────────────────────────────────────────

const WizardSaveProgressOverlay = ({ savePhase, saveProgress, SAVE_PHASE }) => {
  const isSaving  = savePhase === SAVE_PHASE.SAVING;
  const isLinking = savePhase === SAVE_PHASE.LINKING;

  const pct =
    saveProgress && saveProgress.total > 0
      ? Math.round((saveProgress.current / saveProgress.total) * 100)
      : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 4,
        gap: 3,
        minHeight: 260,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'primary.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: 'primary.200',
        }}
      >
        {isLinking ? (
          <LinkIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        ) : (
          <CircularProgress size={28} thickness={3} />
        )}
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          {isLinking ? 'Linking to Jira issue' : 'Saving test cases…'}
        </Typography>
        {isSaving && saveProgress && (
          <Typography variant="body2" color="text.secondary">
            {saveProgress.current} of {saveProgress.total}
            {saveProgress.summary ? ` — ${saveProgress.summary}` : ''}
          </Typography>
        )}
        {isLinking && (
          <Typography variant="body2" color="text.secondary">
            Creating traceability links in Jira
          </Typography>
        )}
      </Box>

      {isSaving && saveProgress && saveProgress.total > 0 && (
        <Box sx={{ width: '100%', maxWidth: 320 }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
          >
            {pct}%
          </Typography>
        </Box>
      )}

      {isLinking && (
        <Box sx={{ width: '100%', maxWidth: 320 }}>
          <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        Please don't close this window
      </Typography>
    </Box>
  );
};

export { WizardSaveProgressOverlay };
export default WizardGeneratingOverlay;